<template>
  <v-modal v-model="showModal" modal-title="İyileştirme Önerisi Düzenle" icon="edit" size="xlg" z-index="100000" :noPadding="true">
    <div v-if="!suggestion" class="p-6 text-center">
      <span class="material-symbols-outlined text-4xl text-gray-400 mb-3">error</span>
      <p class="text-lg font-medium text-gray-900">Öneri Bulunamadı</p>
      <p class="text-sm text-gray-500 mt-1">İstenen öneri bulunamadı veya yüklenemedi.</p>
    </div>

    <div v-else class="flex flex-col h-full">
      <!-- Tab Navigation (Sadece Mobilde Görünür) -->
      <div class="md:hidden flex border-b border-gray-200 bg-gray-50">
        <button @click="activeTab = 'content'" class="flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200" :class="activeTab === 'content' ? 'text-primary border-b-2 border-primary bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'">
          <span class="flex items-center justify-center">
            <span class="material-symbols-outlined mr-1">visibility</span>
            Mevcut İçerik
          </span>
        </button>
        <button @click="activeTab = 'edit'" class="flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200" :class="activeTab === 'edit' ? 'text-primary border-b-2 border-primary bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'">
          <span class="flex items-center justify-center">
            <span class="material-symbols-outlined mr-1">edit_note</span>
            Düzenle
          </span>
        </button>
      </div>

      <!-- Responsive İçerik Alanı - Masaüstünde iki sütun, Mobilde tablar -->
      <div class="flex flex-col md:flex-row flex-grow overflow-hidden">

        <!-- Sol Sütun: Mevcut İçerik Görüntüleme -->
        <div class="w-full md:w-1/2 overflow-y-auto p-4 md:p-6 bg-gray-50 thin-scrollbar" v-show="activeTab === 'content' || isDesktop">
          <!-- İşlem Tarihçesi -->
          <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-gray-50 border-b border-gray-200 flex items-center">
              <span class="material-symbols-outlined text-gray-600 mr-2">history</span>
              <h3 class="font-medium text-gray-800">İşlem Tarihçesi</h3>
            </div>

            <div class="p-5">
              <div class="space-y-3">
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                    <span class="material-symbols-outlined text-green-600 !text-sm">add_circle</span>
                  </div>
                  <div class="flex-grow">
                    <div class="flex items-center text-sm">
                      <span class="font-medium text-gray-900">Oluşturuldu</span> <span class="ml-auto text-xs text-gray-500">{{ new Date(suggestion.createdAt).toLocaleString('tr-TR') }}</span>
                    </div>
                    <p class="text-xs text-gray-600 mt-0.5">
                      {{ suggestion.userName || 'Anonim Kullanıcı' }} tarafından oluşturuldu </p>
                  </div>
                </div>

                <div v-if="suggestion.updatedAt && suggestion.updatedAt !== suggestion.createdAt" class="flex items-start gap-3">
                  <div class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                    <span class="material-symbols-outlined text-blue-600 !text-sm">edit</span>
                  </div>
                  <div class="flex-grow">
                    <div class="flex items-center text-sm">
                      <span class="font-medium text-gray-900">Güncellendi</span> <span class="ml-auto text-xs text-gray-500">{{ new Date(suggestion.updatedAt).toLocaleString('tr-TR') }}</span>
                    </div>
                    <p class="text-xs text-gray-600 mt-0.5">
                      Son güncelleme tarihi </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Öneri Detayı -->
          <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-blue-600 mr-2">description</span> Öneri Detayı </h3>
              <span class="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                {{ getContentTypeLabel(suggestion.contentType) }}
              </span>
            </div>

            <div class="p-5">
              <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {{ suggestion.suggestion }}
              </div>

              <div class="mt-4 flex items-center">
                <div class="flex-shrink-0">
                  <span class="text-sm font-medium mr-2">Öncelik:</span>
                </div>
                <div class="flex items-center gap-2">
                  <div class="h-2 w-20 bg-gray-200 rounded-full overflow-hidden">
                    <div class="h-full" :class="{
                        'bg-green-500': suggestion.priority <= 2,
                        'bg-yellow-500': suggestion.priority === 3,
                        'bg-red-500': suggestion.priority >= 4
                      }" :style="{ width: `${suggestion.priority * 20}%` }"></div>
                  </div>
                  <span class="text-xs font-medium px-2 py-1 rounded-full" :class="{
                      'bg-green-100 text-green-800': suggestion.priority <= 2,
                      'bg-yellow-100 text-yellow-800': suggestion.priority === 3,
                      'bg-red-100 text-red-800': suggestion.priority >= 4
                    }">
                    {{ suggestion.priority }} - {{ ['Çok Düşük', 'Düşük', 'Orta', 'Yüksek', 'Çok Yüksek'][suggestion.priority - 1] }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- İçerik Tipine Göre İçerik Gösterimi -->
          <div v-if="suggestion.contentType === 'IMAGE'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-purple-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-purple-600 mr-2">image</span> Mevcut Görsel </h3>
            </div>

            <div class="p-5">
              <div class="flex justify-center bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 max-h-60 overflow-y-auto">
                <img v-if="suggestion.currentImageUrl" :src="suggestion.currentImageUrl" alt="Mevcut Görsel" class="max-h-48 max-w-full object-contain"/>
                <div v-else class="text-center py-10 text-gray-400">
                  <span class="material-symbols-outlined text-4xl mb-2">image</span>
                  <p>Görsel bulunamadı</p>
                </div>
              </div>

              <div v-if="suggestion.currentImageUrl" class="text-sm text-gray-700 break-all mt-2 max-h-20 overflow-y-auto">
                {{ suggestion.currentImageUrl }}
              </div>
            </div>
          </div>

          <div v-if="suggestion.contentType === 'VIDEO'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-red-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-red-600 mr-2">videocam</span> Mevcut Video </h3>
            </div>

            <div class="p-5">
              <div v-if="suggestion.currentVideoUrl" class="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 max-h-40 overflow-y-auto">
                <div class="flex items-center">
                  <span class="material-symbols-outlined text-red-500 mr-2">play_circle</span>
                  <a :href="suggestion.currentVideoUrl" target="_blank" class="text-primary hover:text-primary-dark break-all text-sm"> {{ suggestion.currentVideoUrl }} </a>
                </div>
              </div>
              <div v-else class="text-center py-10 text-gray-400">
                <span class="material-symbols-outlined text-4xl mb-2">videocam</span>
                <p>Video bulunamadı</p>
              </div>
            </div>
          </div>

          <div v-if="suggestion.contentType === 'TEXT'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-blue-600 mr-2">text_format</span> Mevcut Metin </h3>
              <span v-if="suggestion.currentText" class="text-xs text-gray-500">
                {{ suggestion.currentText.length.toLocaleString('tr-TR') }} karakter
              </span>
            </div>

            <div class="p-5">
              <div v-if="suggestion.currentText" class="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                <div class="prose max-w-none text-sm" v-html="suggestion.currentText"></div>
              </div>
              <div v-else class="text-center py-10 text-gray-400">
                <span class="material-symbols-outlined text-4xl mb-2">article</span>
                <p>Metin içeriği bulunamadı</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sağ Sütun: Düzenleme Formu -->
        <div class="w-full md:w-1/2 overflow-y-auto p-4 md:p-6 border-l-0 md:border-l border-gray-200" v-show="activeTab === 'edit' || isDesktop">
          <!-- Öneri Formu -->
          <div class="space-y-3">
            <div class="bg-white rounded-lg shadow-sm p-5 border border-primary border-opacity-20">
              <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
                <div class="flex items-center">
                  <span class="material-symbols-outlined text-primary mr-2">edit_note</span>
                  <h3 class="font-medium text-gray-800">Öneri Detayları</h3>
                </div>
                <span class="text-xs px-2 py-1 bg-primary bg-opacity-10 text-primary rounded-full self-start sm:self-auto">
                  {{ getContentCategoryLabel(suggestion.contentCategory) }}
                </span>
              </div>

              <!-- Öneri Metni -->
              <div class="mb-5">
                <div class="flex justify-between mb-2">
                  <label class="block text-sm font-semibold text-gray-700"> Öneri Detayı <span class="text-red-500">*</span> </label> <span class="text-xs text-gray-500">
                    <span :class="{'text-red-500': form.suggestion.length > 500, 'text-green-600': form.suggestion.length > 0 && form.suggestion.length <= 500}">{{ form.suggestion.length }}</span> / 500
                  </span>
                </div>

                <v-input v-model="form.suggestion" type="textarea" placeholder="İçerik için iyileştirme önerinizi detaylı olarak açıklayın." :error="errors.suggestion" textAreaHeight="h-32" required class="transition-all duration-200 focus-within:shadow-sm"/>
              </div>

              <!-- Öncelik Ayarı -->
              <div class="bg-gray-50 rounded-lg p-4 mb-5">
                <div class="flex justify-between items-center mb-3">
                  <label class="text-sm font-semibold text-gray-700 flex items-center"> <span class="material-symbols-outlined text-amber-500 mr-1 !text-sm">priority_high</span> Öneri Önceliği
                  </label> <span class="text-xs font-medium px-2 py-1 rounded-full" :class="{
                      'bg-green-100 text-green-800': form.priority <= 2,
                      'bg-yellow-100 text-yellow-800': form.priority === 3,
                      'bg-red-100 text-red-800': form.priority >= 4
                    }">
                    {{ ['Çok Düşük', 'Düşük', 'Orta', 'Yüksek', 'Çok Yüksek'][form.priority - 1] }}
                  </span>
                </div>

                <div class="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <div class="flex items-center gap-1 md:gap-2 justify-center sm:justify-start">
                    <button v-for="priority in [1, 2, 3, 4, 5]" :key="priority" type="button" @click="form.priority = priority" class="w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs md:text-sm transition-all hover:transform hover:scale-110" :class="{
                        'bg-green-100 text-green-800 ring-2 ring-green-400 shadow-sm': form.priority === priority && priority <= 2,
                        'bg-yellow-100 text-yellow-800 ring-2 ring-yellow-400 shadow-sm': form.priority === priority && priority === 3,
                        'bg-red-100 text-red-800 ring-2 ring-red-400 shadow-sm': form.priority === priority && priority >= 4,
                        'bg-gray-100 text-gray-600 hover:bg-gray-200': form.priority !== priority
                      }">
                      {{ priority }}
                    </button>
                  </div>

                  <div class="w-full sm:w-40">
                    <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div class="h-full transition-all duration-300" :class="{
                          'bg-green-500': form.priority <= 2,
                          'bg-yellow-500': form.priority === 3,
                          'bg-red-500': form.priority >= 4
                        }" :style="{width: (form.priority * 20) + '%'}"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- İçerik Tipine Özel Alanlar -->
            <div v-if="suggestion.contentType === 'IMAGE'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-3">
                <span class="material-symbols-outlined text-purple-600 mr-2">image</span>
                <h3 class="font-medium text-gray-800">Önerilen Görsel Linki</h3>
              </div>

              <label class="block text-sm font-medium text-gray-700 mb-2">Önerilen Görsel URL'i</label>
              <v-input v-model="form.currentImageUrl" :error="errors.currentImageUrl" placeholder="https://example.com/iyilestirilmis-gorsel.jpg" showIcon icon="image" class="transition-all duration-200 focus-within:shadow-sm"/>
              <p class="text-xs text-gray-500 mt-2 flex items-start">
                <span class="material-symbols-outlined !text-xs mr-1 mt-0.5">info</span> <span>Görsel URL'i <b>http://</b> veya <b>https://</b> ile başlamalıdır.</span>
              </p>
            </div>

            <div v-if="suggestion.contentType === 'VIDEO'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-4">
                <span class="material-symbols-outlined text-red-600 mr-2">videocam</span>
                <h3 class="font-medium text-gray-800">Önerilen Video Linki</h3>
              </div>

              <label class="block text-sm font-medium text-gray-700 mb-2">Önerilen Video URL'i</label>
              <v-input v-model="form.currentVideoUrl" :error="errors.currentVideoUrl" placeholder="https://example.com/iyilestirilmis-video.mp4" showIcon icon="videocam" class="transition-all duration-200 focus-within:shadow-sm"/>
              <p class="text-xs text-gray-500 mt-2 flex items-start">
                <span class="material-symbols-outlined !text-xs mr-1 mt-0.5">info</span> <span>YouTube, Vimeo veya diğer video paylaşım platformlarının linklerini kullanabilirsiniz.</span>
              </p>

              <div class="flex justify-end mt-3">
                <a v-if="form.currentVideoUrl" :href="form.currentVideoUrl" target="_blank" class="inline-flex items-center px-3 py-1 bg-primary text-white rounded hover:bg-primary-dark text-sm">
                  <span class="material-symbols-outlined !text-sm mr-1">play_circle</span> Video Önizleme </a>
              </div>
            </div>

            <div v-if="suggestion.contentType === 'TEXT'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-3">
                <span class="material-symbols-outlined text-blue-600 mr-2">text_format</span>
                <h3 class="font-medium text-gray-800">İyileştirilmiş Metin</h3>
              </div>

              <div class="flex justify-between mb-2">
                <label class="block text-sm font-medium text-gray-700">İçerik</label> <span class="text-xs text-gray-500">
                  {{ form.currentText ? form.currentText.length.toLocaleString('tr-TR') : '0' }} karakter
                </span>
              </div>

              <v-input v-model="form.currentText" type="textarea" placeholder="İyileştirilmiş metin içeriğini buraya yazın..." textAreaHeight="h-40" class="transition-all duration-200 focus-within:shadow-sm"/>

              <p class="text-xs text-gray-500 mt-2 flex items-center">
                <span class="material-symbols-outlined !text-xs mr-1">info</span> <span>HTML formatında metin düzenlemesi yapılabilir.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Relative -->
      <div class="border-t border-gray-200 bg-gray-50 p-3 flex justify-between items-center relative mt-auto z-10 shadow-md">
        <div class="text-xs text-gray-500">
          <span class="material-symbols-outlined text-amber-500 mr-1 text-sm align-text-bottom">update</span> Son güncelleme:
          {{ suggestion.updatedAt ? new Date(suggestion.updatedAt).toLocaleString('tr-TR') : 'Henüz güncellenmedi' }}
        </div>

        <div class="flex gap-2">
          <v-button @click="closeModal" button-text="İptal" color="secondary" type="button" icon="close" size="sm" :disabled="isSubmitting" class="hover:shadow-sm"/>
          <v-button @click="handleSubmit" button-text="Değişiklikleri Kaydet" color="primary" :is-loading="isSubmitting" loading-text="Kaydediliyor..." type="button" icon="save" :disabled="!isFormValid || isSubmitting" size="sm" class="hover:shadow-sm shadow-xs"/>
        </div>
      </div>
    </div>
  </v-modal>
</template>

<script setup>
import { computed, watch, reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import VModal from '@/components/modals/VModal.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import VInput from '@/components/base/inputs/VInput.vue'
import { useSuggestionEdit } from '../../composables'
import {
  getContentTypeLabel,
  getContentCategoryLabel,
  getContentTypeIcon,
  getContentTypeIconClass,
} from '../../constants/suggestions'

// Mobil tab yönetimi için
const activeTab = ref('content') // 'content' veya 'edit'

// Desktop için responsive kontrol
const isDesktop = ref(false)
const checkScreenSize = () => {
  isDesktop.value = window.innerWidth >= 768 // md breakpoint
}

// Component yüklenirken ve pencere boyutu değiştiğinde screen size kontrolü
onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
})

// Öneri düzenleme composable'ını kullan
const {
  form,
  errors,
  isSubmitting,
  imageLoadError,
  isFormValid,
  reloadImage,
  handleImageError,
  submitForm,
  loadSuggestionData,
} = useSuggestionEdit()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  suggestion: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'updated'])

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Form gönderimi için wrapper fonksiyon
const handleSubmit = async () => {
  if (!props.suggestion) return

  await submitForm(
      props.suggestion,
      // Başarı durumunda
      (result) => {
        emit('updated', result)
        closeModal()
      },
      // Hata durumunda
      (error) => {
        console.error('Error updating suggestion:', error)
      },
  )
}

const closeModal = () => {
  showModal.value = false
}

// Suggestion değişikliklerini izle ve formu güncelle
watch(() => props.suggestion, (newVal) => {
  if (newVal) {
    loadSuggestionData(newVal)
  }
}, { immediate: true })
</script>
